/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
// libraries
import { Box, Button, Typography, Container, Stack, Tooltip, Zoom, Collapse, IconButton, Chip } from '@mui/material';
import { isEmpty } from 'lodash';
import { LoadingButton } from '@mui/lab';
// components
import SimilarLeadWarningPopup from '../../sections/@dashboard/leadDetail/SimilarLeadWarningPopup';
import FollowUpChecklistV2 from '../../sections/@dashboard/leadDetail/FollowUpChecklistV2';
import AttendedGuidedChecklist from '../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/AttendedGuidedChecklist';
import AttendedGuidedChecklistV2 from '../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/v2/AttendedGuidedChecklistV2';
import AttendanceGate from '../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/AttendanceGate';
import useGuidedChecklist from '../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/hooks/useGuidedChecklist';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { FormProvider } from '../hook-form';
import Iconify from '../Iconify';
import ClinCheckForm from '../clinCheck/ClinCheckForm';
import { NewAppointment, TreatmentComplete, Archive } from '../tabs';
import { AssignUserAvatar } from '../custom-avatar';
// store
import { dispatch, useSelector } from '../../redux/store';
import { saveNewLeadDetail } from '../../redux/slices/lead';
import { updateError } from '../../redux/slices/user';
import { clearClinCheckData, getClinCheckList } from '../../redux/slices/clincheck';
// context/utils/constant/hooks
import { useSettingsContext } from '../settings/context';
import capitalizeFirstLetter from '../../utils/capitalizedFunc';
import { getTreatmentDate } from '../../utils/multiTreatmentFlow';
import { isDisabledTabForFinishButton } from '../../utils/clinCheckUtils';
import { useTabs } from '../../hooks';
// style
import { attendedFooterStyle } from '../../styles/AttendedAppointment';
import TCOScript from './TCOScript';
import AssignedUsersShow from '../AssignedUsersShow';
import ScheduleTaskReminder from './ScheduleTaskReminder';
import resolveLeadTreatmentPrice from '../../utils/resolveLeadTreatmentPrice';

// Re-export under the legacy name so existing imports don't break during transition.
export const resolveAttendedTreatmentPrice = ({ followLead, filterStatus = 'CONSULTATION_ATTENDED' } = {}) =>
    resolveLeadTreatmentPrice({ followLead, filterStatus });

const AttendedAppointment = forwardRef(({ isOverview, type, id, handleClose, user, OpenAssignTask, editDetailHandler, handleCloseAndReload, handleReloadLeads, isFinishButtonAvailable, handleNoAttended, onOpenSidebarTab }, ref) => {
    const attendedRef = useRef();
    const { currentTab, onChangeTab } = useTabs('');
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { followLead, currentTreatmentData } = useSelector((state) => state.lead);
    const { practice } = useSelector((state) => state.practice);
    const settings = useSettingsContext();
    const [activeTab, setActiveTab] = useState('');
    const [depositPendingStatus, setDepositPendingStatus] = useState(false);
    const { clinCheckListing } = useSelector((state) => state.clinCheck);
    const clinCheckData = clinCheckListing?.results?.[0];
    const followLeadFilterAttend = followLead?.lead_appointment?.filter((el) => el.status === 'BOOKED' || el.status === 'CONSULTATION_BOOKED');

    const { isLoader } = useSelector((state) => state.loaderReducer);

    const { currentTreatmentDataGlobal } = useSelector((state) => state.multiTreatment);

    const [assigneeSelected, setAssigneeSelected] = useState(followLead?.assignees || []);
    const [infoExpanded, setInfoExpanded] = useState(false);
    const [pickerCollapsed, setPickerCollapsed] = useState(false);
    // Skip gate on re-open if lead is already CONSULTATION_ATTENDED.
    const initiallyAttended = (followLead?.lead_treatments || user?.lead_treatments || []).some(
        (t) => t.status === 'CONSULTATION_ATTENDED'
    );
    const [gateDismissed, setGateDismissed] = useState(initiallyAttended);

    const v2LeadId = followLead?.id || user?.lead_id || user?.id;
    const { initialLoading: gcInitialLoading, templatesEmpty } = useGuidedChecklist({ practiceId, leadId: v2LeadId, leadDetail: followLead });
    // Gate on initialLoading (not loading) so post-action refreshes don't unmount V2 mid-action
    // and discard its local state — parity with ConsultationAttendedAppointment.
    const v2GuidedActive = hasFeatureAccess('tasks-v2', practiceId) && !gcInitialLoading && !templatesEmpty;

    // V2 guided checklist is now the DEFAULT experience for all practices.
    // Escape hatches (legacy v1): ?guidedV=1 in the URL, or a practice preference
    // guided_checklist_version === 'v1'. ?guidedV=2 always forces v2.
    const guidedParam = new URLSearchParams(window.location.search).get('guidedV');
    const practiceGuidedVersion = practice?.preferences?.guided_checklist_version;
    const useV2Guided = guidedParam === '2'
        || (guidedParam !== '1' && practiceGuidedVersion !== 'v1');
    const isLeadBooked = (followLead?.lead_treatments || []).some((t) => t.status === 'CONSULTATION_BOOKED');

    useEffect(() => {
        if (type !== 'CreatedLead' && !isEmpty(currentTreatmentData)) {
            setDepositPendingStatus(currentTreatmentData.is_treatment_fee_pending);
        }
    }, [currentTreatmentData]);

    const isPaymentMandatory = practice?.preferences?.is_payment_enforced_before_treatment;

    useEffect(() => {
        if (type !== 'CreatedLead') {
            const leadId = followLead?.id || user?.lead_id || user?.id;
            const treatmentId = followLead?.lead_treatments?.[0]?.id || user?.treatment_id || user?.lead_treatments?.[0]?.id;
            const data = {
                leadId,
                treatmentId,
                practiceId
            };
            dispatch(clearClinCheckData());
            if (leadId && treatmentId) {
                dispatch(getClinCheckList(data));
            }
        }
    }, []);

    const defaultValues = {
        is_clinical_photographs_taken: false,
        is_oral_health_assessment_taken: false,
        is_xray_taken: false,
        is_digital_scan_taken: false,
        is_impressions_taken: false,
        is_treatment_options_discussed: false,
        is_treatment_plan_confirmed: false,
        is_finance_options_discussed: false,
        notes: ''
    };

    const methods = useForm({
        defaultValues,
    });

    const {
        handleSubmit
    } = methods;

    const appointmentNotesUpdate = async (noteData, callAppointmentNote) => {
        const noteDetail = {
            additional_notes: noteData.notes,
            clinical_attachments: [
                {
                    is_clinical_photographs_taken: noteData.is_clinical_photographs_taken,
                    is_oral_health_assessment_taken: noteData.is_oral_health_assessment_taken,
                    is_xray_taken: noteData.is_xray_taken
                }
            ],
            assessment_attachments: [
                {
                    is_digital_scan_taken: noteData.is_digital_scan_taken,
                    is_impressions_taken: noteData.is_impressions_taken,
                    is_treatment_options_discussed: noteData.is_treatment_options_discussed,
                    is_treatment_plan_confirmed: noteData.is_treatment_plan_confirmed,
                    is_finance_options_discussed: noteData.is_finance_options_discussed
                }
            ]
        };
        dispatch(saveNewLeadDetail({ attentendAppoiment: { noteDetail, activeTab, callAppointmentNote } }));
        await attendedRef.current.submit('finish');
    };

    useImperativeHandle(
        ref,
        () => ({
            async submit() {
                await handleSubmit(onSubmit)();
            },
        }),
        []
    );

    const onSubmit = async (data) => {
        if (!activeTab) {
            dispatch(updateError({ message: 'Please select any one Tab', success: false }));
            return;
        }
        const callAppointmentNote = JSON.stringify(data) !== JSON.stringify(defaultValues);
        appointmentNotesUpdate(data, callAppointmentNote);
    };

    const handleNext = async () => {
        handleSubmit(onSubmit)();
    };

    useImperativeHandle(
        ref, () => ({
            async submit(status) {
                await handleSubmit((data) => onSubmit(data, status))();
            },
        }),
        [activeTab]
    );

    const getCurrentTreatmentData = () => {
        if (isOverview) {
            return { selectedTreatmentData: currentTreatmentDataGlobal };
        }
        return {};
    };

    // Reminder & Follow-up chip content.
    //
    // V2 practices with a template applied → show the V2 guided checklist
    //   (AttendedGuidedChecklistV2 / AttendedGuidedChecklist) for the task-display
    //   portion; the new ScheduleTaskReminder is appended below the checklist so
    //   users can also create a standalone follow-up task when no checklist action
    //   is outstanding.
    //
    // V2 practices with NO template applied (v2GuidedActive is false because
    //   templatesEmpty) → show ScheduleTaskReminder directly (fixes the
    //   "blank Reminder & Follow-up tab" on lead 152413).
    //
    // Non-V2 practices → unchanged V1 FollowUpChecklistV2 fallback.
    const onTaskActivity = () => {
        if (!activeTab) setActiveTab('Reminder & Follow-up');
    };
    const reminderTabComponent = (() => {
        const isV2Practice = hasFeatureAccess('tasks-v2', practiceId);

        // ── Non-V2 practice: leave V1 path untouched ──────────────────────────
        if (!isV2Practice) {
            return (
                <FollowUpChecklistV2
                    practiceId={practiceId}
                    leadId={followLead?.id || user?.lead_id || user?.id}
                    leadDetail={followLead || { id: user?.lead_id || user?.id }}
                    handleClose={handleClose}
                />
            );
        }

        // ── V2 practice, no template applied: show ScheduleTaskReminder only ──
        if (!v2GuidedActive) {
            return (
                <ScheduleTaskReminder
                    practiceId={practiceId}
                    leadId={followLead?.id || user?.lead_id || user?.id}
                    leadDetail={followLead}
                    onTaskActivity={onTaskActivity}
                    handleClose={handleClose}
                    handleReloadLeads={handleReloadLeads}
                />
            );
        }

        // ── V2 practice with template: show guided checklist + reminder form ──
        const guidedChecklist = useV2Guided ? (
            <AttendedGuidedChecklistV2
                mode="gate-first"
                practiceId={practiceId}
                leadId={v2LeadId}
                leadDetail={followLead}
                handleClose={handleClose}
                handleNoAttended={handleNoAttended}
                gateRenderedInHost
                externalGateDismissed={gateDismissed}
                onTaskActivity={onTaskActivity}
                onOpenSidebarTab={onOpenSidebarTab}
            />
        ) : (
            <AttendedGuidedChecklist
                mode="gate-first"
                practiceId={practiceId}
                leadId={v2LeadId}
                leadDetail={followLead}
                handleClose={handleClose}
                handleNoAttended={handleNoAttended}
                gateRenderedInHost
                externalGateDismissed={gateDismissed}
                handleReloadLeads={handleReloadLeads}
                onTaskActivity={onTaskActivity}
            />
        );
        return (
            <>
                {guidedChecklist}
                <ScheduleTaskReminder
                    practiceId={practiceId}
                    leadId={followLead?.id || user?.lead_id || user?.id}
                    leadDetail={followLead}
                    onTaskActivity={onTaskActivity}
                    handleClose={handleClose}
                    handleReloadLeads={handleReloadLeads}
                />
            </>
        );
    })();

    const PROFILE_TABS = [
        {
            // Leftmost chip — opens the V2 Guided Checklist (or V1 FollowUpChecklistV2 fallback
            // when the tasks-v2 feature flag is off / templates are empty).
            value: 'Reminder & Follow-up',
            icon: <Iconify icon={'solar:bell-bold-duotone'} width={20} height={20} />,
            component: reminderTabComponent,
        },
        {
            value: 'Starting treatment',
            icon: <Iconify icon={'solar:medical-kit-bold-duotone'} width={20} height={20} />,
            component: <NewAppointment {...getCurrentTreatmentData()} ref={attendedRef} type={activeTab} popupType={type} rowId={id} handleClose={handleClose} handleReloadLeads={handleReloadLeads} />,
        },
        {
            value: 'ClinCheck',
            icon: <Iconify icon={'fluent:check-20-filled'} width={20} height={20} />,
            component: <ClinCheckForm ref={attendedRef} handleClose={handleClose} handleCloseAndReload={handleCloseAndReload} leadDetail={user} currentTab={currentTab} onChangeTab={onChangeTab} />,
        },
        {
            value: 'Treatment complete',
            icon: <Iconify icon={'akar-icons:circle-check-fill'} width={20} height={20} />,
            component: <TreatmentComplete ref={attendedRef} type={type} handleNext={handleNext} handleClose={handleClose} />,
        },
        {
            value: 'Archive',
            icon: <Iconify icon={'eva:archive-fill'} width={20} height={20} />,
            component: <Archive ref={attendedRef} handleClose={handleClose} screenType={type} handleReloadLeads={handleReloadLeads} />,
        }
    ];

    const getAttendedComponents = () => {
        if (type === 'CreatedLead') {
            return PROFILE_TABS.filter(el => el.value !== 'Archive' && el.value !== 'ClinCheck');
        }
        if (type === 'Starting treatment' && followLead?.lead_treatments?.[0]?.treatment === 'Invisalign') {
            return PROFILE_TABS;
        }
        return PROFILE_TABS.filter(el => el.value !== 'ClinCheck');
    };

    const getIsVisible = () => {
        if (isFinishButtonAvailable) {
            return {};
        }
        return { display: 'none' };
    };

    const getSelectedTreatment = () => {
        if (isOverview) return currentTreatmentDataGlobal?.treatmentData?.treatment || followLead?.lead_treatments?.[0]?.treatment;
        return followLead?.lead_treatments?.[0]?.treatment;
    };

    const getSelectedTreatmentDateTime = () => {
        const tempDate = followLead?.lead_appointment?.find((ele) => ele?.lead_treatment === currentTreatmentDataGlobal?.treatmentData?.id)?.datetime;
        if (isOverview && tempDate) return getTreatmentDate(tempDate) || '';
        return getTreatmentDate(followLeadFilterAttend?.[0]?.datetime) || '';
    };

    const getCurrentTreatmentAssignees = () => {
        if (isOverview) {
            const found = followLead?.lead_appointment?.find((ele) => ele?.lead_treatment === currentTreatmentDataGlobal?.treatmentData?.id);
            return found?.assignees || followLead?.lead_appointment?.[0]?.assignees;
        }
        return followLeadFilterAttend?.[0]?.assignees;
    };

    useEffect(() => {
        if (followLead?.assignees) {
            setAssigneeSelected(followLead.assignees || []);
        }
    }, [followLead]);

    // Auto-collapse info card when an outcome tab is selected
    useEffect(() => {
        if (activeTab) {
            setInfoExpanded(false);
        }
    }, [activeTab]);

    return (
        <Box>
            {type !== 'CreatedLead' && <SimilarLeadWarningPopup type={'DialogPopup'} dialogClose={() => handleClose()} />}
            <Box sx={{ flex: 'none' }}>
                <TCOScript handleClose={handleClose} handleNext={onSubmit} ref={attendedRef} />
            </Box>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                {user !== undefined && user && <Box sx={{ px: 3, py: 2, mt: 1, mb: 2, bgcolor: '#ffffff', borderRadius: '12px' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                                <Chip
                                    size="small"
                                    icon={<Iconify icon="mdi:calendar-clock" width={13} height={13} sx={{ color: '#f59e0b !important', ml: '6px !important' }} />}
                                    label="Booked"
                                    sx={{
                                        height: 22,
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        bgcolor: 'rgba(245, 158, 11, 0.10)',
                                        color: '#f59e0b',
                                        letterSpacing: '-0.005em',
                                        border: 'none',
                                        '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                            </Stack>
                            <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.026em', lineHeight: 1.15 }}>
                                {capitalizeFirstLetter(followLead?.first_name || '')} {capitalizeFirstLetter(followLead?.last_name || '')}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap' }}>
                                <Chip
                                    size="small"
                                    icon={<Iconify icon="mdi:tooth-outline" width={13} height={13} sx={{ color: '#078DEE !important', ml: '6px !important' }} />}
                                    label={getSelectedTreatment() || '—'}
                                    sx={{
                                        height: 24, fontSize: '12px', fontWeight: 500,
                                        bgcolor: 'rgba(7, 141, 238, 0.08)', color: '#078DEE',
                                        border: 'none', '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                                <Chip
                                    size="small"
                                    icon={<Iconify icon="mdi:calendar-clock" width={13} height={13} sx={{ color: '#00AB55 !important', ml: '6px !important' }} />}
                                    label={getSelectedTreatmentDateTime(followLeadFilterAttend) || '—'}
                                    sx={{
                                        height: 24, fontSize: '12px', fontWeight: 500,
                                        bgcolor: 'rgba(0, 171, 85, 0.08)', color: '#00AB55',
                                        border: 'none', '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                                {(() => {
                                    const assignees = getCurrentTreatmentAssignees(followLeadFilterAttend);
                                    if (!assignees?.length) return null;
                                    return (
                                        <Tooltip title={`Practitioner: ${assignees.map(a => `${a?.user?.first_name || a?.first_name || ''} ${a?.user?.last_name || a?.last_name || ''}`.trim()).filter(Boolean).join(', ')}`} placement="bottom" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                                            <Stack direction="row" alignItems="center" spacing={-0.5} sx={{ ml: 0.5 }}>
                                                {assignees.slice(0, 2).map((a, i) => (
                                                    <AssignUserAvatar
                                                        key={i}
                                                        sx={{ height: 22, width: 22, fontSize: '10px', border: '1.5px solid white' }}
                                                        src={a?.user?.avatar || ''}
                                                        name={`${a?.user?.first_name || a?.first_name || ''} ${a?.user?.last_name || a?.last_name || ''}`}
                                                        role={a?.role}
                                                    />
                                                ))}
                                            </Stack>
                                        </Tooltip>
                                    );
                                })()}
                                {assigneeSelected?.length > 0 && (
                                    <Tooltip title={`Assignees: ${assigneeSelected.map(a => `${a.user?.first_name || ''} ${a.user?.last_name || ''}`.trim()).filter(Boolean).join(', ')}`} placement="bottom" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                                        <Stack direction="row" alignItems="center" spacing={-0.5}>
                                            {assigneeSelected.slice(0, 2).map((ele, i) => (
                                                <AssignUserAvatar
                                                    key={i}
                                                    sx={{ height: 22, width: 22, fontSize: '10px', border: '1.5px solid white' }}
                                                    src={ele.user?.avatar || ''}
                                                    name={`${ele.user?.first_name || ''} ${ele.user?.last_name || ''}`}
                                                    role={ele?.role}
                                                />
                                            ))}
                                        </Stack>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Box>
                        <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ flexShrink: 0 }}>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '0.02em', textTransform: 'uppercase', fontWeight: 500, mb: 0.25 }}>
                                    Price
                                </Typography>
                                <Typography data-testid="attended-treatment-price" sx={{ fontSize: '24px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.026em', lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
                                    {(() => {
                                        const price = resolveLeadTreatmentPrice({ followLead });
                                        if (price == null) return '—';
                                        return `£${Number(price).toLocaleString('en-GB')}`;
                                    })()}
                                </Typography>
                            </Box>
                            <Stack direction="column" spacing={0.25} sx={{ ml: 0.5 }}>
                                <IconButton
                                    size="small"
                                    onClick={() => editDetailHandler()}
                                    sx={{ color: '#6e6e73', '&:hover': { bgcolor: 'rgba(0,113,227,0.06)', color: '#0071e3' } }}
                                >
                                    <Iconify icon="solar:pen-bold" width={15} height={15} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => setInfoExpanded(!infoExpanded)}
                                    sx={{ color: '#6e6e73', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: '#1d1d1f' } }}
                                >
                                    <Iconify icon={infoExpanded ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} width={18} height={18} />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Stack>
                    <Collapse in={infoExpanded} timeout={300}>
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ height: '0.5px', bgcolor: 'rgba(0,0,0,0.10)', mb: 2 }} />
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                {(followLead?.phone || followLead?.lead_contact?.phone) && (
                                    <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em' }}>
                                        {followLead?.phone || followLead?.lead_contact?.phone}
                                    </Typography>
                                )}
                                {followLead?.lead_address && (
                                    <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em' }}>
                                        · {[followLead?.lead_address?.address_line_1, followLead?.lead_address?.city, followLead?.lead_address?.postcode].filter(Boolean).join(', ')}
                                    </Typography>
                                )}
                            </Stack>
                            <Stack direction="row" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ gap: 4 }}>
                                <Box sx={{ flex: 1, minWidth: 160 }}>
                                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                                        <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '-0.005em' }}>Practitioner</Typography>
                                        <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => OpenAssignTask(user, getCurrentTreatmentAssignees(followLeadFilterAttend), e)}>
                                            <Iconify width={12} height={12} icon={'solar:pen-bold'} sx={{ color: '#6e6e73' }} />
                                        </IconButton>
                                    </Stack>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#1d1d1f', letterSpacing: '-0.01em' }}>
                                        {(() => {
                                            const assignees = getCurrentTreatmentAssignees(followLeadFilterAttend);
                                            const names = assignees?.map(a => `${a?.user?.first_name || a?.first_name || ''} ${a?.user?.last_name || a?.last_name || ''}`.trim()).filter(Boolean) || [];
                                            if (names.length === 0) return '—';
                                            if (names.length === 1) return names[0];
                                            return `${names[0]} + ${names.length - 1}`;
                                        })()}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 160 }}>
                                    <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '-0.005em', mb: 0.5 }}>Assignees</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {assigneeSelected?.length > 0 ? assigneeSelected.slice(0, 4).map((ele, index) => (
                                            <Tooltip key={index} title={`${ele.user?.first_name || ''} ${ele.user?.last_name || ''}`} placement='bottom' TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                                                <AssignUserAvatar
                                                    sx={{ height: 28, width: 28, fontSize: '11px' }}
                                                    src={ele.user?.avatar || ''}
                                                    name={`${ele.user?.first_name || ''} ${ele.user?.last_name || ''}`}
                                                    role={ele?.role}
                                                />
                                            </Tooltip>
                                        )) : <Typography sx={{ fontSize: '13px', color: '#6e6e73' }}>—</Typography>}
                                        {assigneeSelected?.length > 4 && (
                                            <Typography sx={{ fontSize: '11px', color: '#6e6e73', ml: 0.5 }}>+{assigneeSelected.length - 4}</Typography>
                                        )}
                                        <AssignedUsersShow assigneeSelected={assigneeSelected} setAssigneeSelected={setAssigneeSelected} leadId={followLead?.id || user?.lead_id || user?.id} compact />
                                    </Box>
                                </Box>
                            </Stack>
                        </Box>
                    </Collapse>
                    {v2GuidedActive && isLeadBooked && (
                        <Collapse in={!gateDismissed} timeout={320} unmountOnExit>
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                                <AttendanceGate
                                    variant="inline-hero"
                                    leadDetail={followLead}
                                    handleNoAttended={handleNoAttended}
                                    onDismissed={() => setGateDismissed(true)}
                                />
                            </Box>
                        </Collapse>
                    )}
                </Box>}
            </FormProvider>
            {/* V2 content surfaced inline once the attendance gate is dismissed and no
                outcome chip has been chosen. When v2GuidedActive=true the guided checklist
                (+ ScheduleTaskReminder) shows. When v2GuidedActive=false but the practice
                has tasks-v2 (no template applied), ScheduleTaskReminder alone shows —
                fixing the blank-tab case for leads with no checklist template. */}
            {hasFeatureAccess('tasks-v2', practiceId) && gateDismissed && !activeTab && (
                <Box className="gchk-inline-popup-wrap" sx={{ mb: 2, width: '100%' }}>
                    {reminderTabComponent}
                </Box>
            )}
            <Box sx={{ mt: 0, mb: 2, p: pickerCollapsed ? 2 : 3, bgcolor: '#ffffff', borderRadius: '12px', width: '100%', transition: 'padding 450ms cubic-bezier(0.32, 0.72, 0, 1)' }}>
                {v2GuidedActive && (
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6e6e73', mb: 1.5 }}>
                        What&apos;s next for this lead?
                    </Typography>
                )}
                <Collapse
                    in={!pickerCollapsed}
                    timeout={450}
                    easing={{ enter: 'cubic-bezier(0.32, 0.72, 0, 1)', exit: 'cubic-bezier(0.32, 0.72, 0, 1)' }}
                >
                    <Box>
                        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.022em', lineHeight: 1.2 }}>
                            What&apos;s next?
                        </Typography>
                        <Typography sx={{ fontSize: '13px', color: '#6e6e73', mt: 0.25, mb: 2 }}>
                            Choose the outcome for this appointment.
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                gap: 1.5,
                            }}
                        >
                            {getAttendedComponents().map((tab) => {
                                const isActive = activeTab === tab.value;
                                const OUTCOME_META = {
                                    'Reminder & Follow-up': { desc: 'Schedule another touchpoint', color: '#FFAB00', bg: 'rgba(255,171,0,0.06)' },
                                    'Starting treatment': { desc: 'Patient is ready — begin the plan', color: '#00AB55', bg: 'rgba(0,171,85,0.06)' },
                                    'ClinCheck': { desc: 'Send for Invisalign approval', color: '#078DEE', bg: 'rgba(7,141,238,0.06)' },
                                    'Treatment complete': { desc: 'Treatment finished today', color: '#7B61FF', bg: 'rgba(123,97,255,0.06)' },
                                    'Archive': { desc: 'Lead is not progressing', color: '#FF5630', bg: 'rgba(255,86,48,0.06)' },
                                };
                                const meta = OUTCOME_META[tab.value] || { desc: '', color: '#078DEE', bg: 'rgba(7,141,238,0.06)' };
                                return (
                                    <Box
                                        key={tab.value}
                                        onClick={() => { setActiveTab(tab.value); setPickerCollapsed(true); }}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: { xs: 0.5, sm: 1 },
                                            px: { xs: 1, sm: 2 },
                                            py: { xs: 1.5, sm: 2.5 },
                                            borderRadius: '12px',
                                            minHeight: { xs: 90, sm: 140 },
                                            cursor: 'pointer',
                                            transition: 'border-color 250ms cubic-bezier(0.32, 0.72, 0, 1), box-shadow 250ms cubic-bezier(0.32, 0.72, 0, 1), transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                                            background: meta.bg,
                                            border: isActive ? `1.5px solid ${meta.color}` : '1px solid transparent',
                                            boxShadow: isActive ? `0 0 0 1px ${meta.color}22` : 'none',
                                            justifyContent: 'center',
                                            userSelect: 'none',
                                            '&:hover': { borderColor: meta.color, boxShadow: `0 0 0 1px ${meta.color}22` },
                                            '&:active': { transform: 'scale(0.96)' },
                                        }}
                                    >
                                        <Box sx={{
                                            width: { xs: 36, sm: 48 },
                                            height: { xs: 36, sm: 48 },
                                            borderRadius: { xs: '10px', sm: '12px' },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: '#fff',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                        }}>
                                            <Iconify icon={tab.icon.props.icon} width={{ xs: 18, sm: 24 }} sx={{ color: meta.color }} />
                                        </Box>
                                        <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 600, color: '#212B36', whiteSpace: 'nowrap' }}>
                                            {tab.value}
                                        </Typography>
                                        <Typography sx={{ fontSize: { xs: '10px', sm: '11px' }, color: '#919EAB', fontWeight: 400, textAlign: 'center', lineHeight: 1.3, display: { xs: 'none', sm: 'block' } }}>
                                            {meta.desc}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Collapse>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        opacity: pickerCollapsed && !!activeTab ? 1 : 0,
                        maxHeight: pickerCollapsed && !!activeTab ? '64px' : 0,
                        transform: pickerCollapsed && !!activeTab ? 'scale(1)' : 'scale(0.96)',
                        transformOrigin: 'center',
                        overflow: 'hidden',
                        pointerEvents: pickerCollapsed && !!activeTab ? 'auto' : 'none',
                        transition: 'opacity 450ms cubic-bezier(0.32, 0.72, 0, 1), max-height 450ms cubic-bezier(0.32, 0.72, 0, 1), transform 450ms cubic-bezier(0.32, 0.72, 0, 1)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                        {(() => {
                            const OUTCOME_META = {
                                'Reminder & Follow-up': { color: '#FFAB00', bg: 'rgba(255,171,0,0.10)' },
                                'Starting treatment': { color: '#00AB55', bg: 'rgba(0,171,85,0.10)' },
                                'ClinCheck': { color: '#078DEE', bg: 'rgba(7,141,238,0.10)' },
                                'Treatment complete': { color: '#7B61FF', bg: 'rgba(123,97,255,0.10)' },
                                'Archive': { color: '#FF5630', bg: 'rgba(255,86,48,0.10)' },
                            };
                            const allTabs = getAttendedComponents();
                            const selectedTab = allTabs.find(t => t.value === activeTab);
                            if (!selectedTab) return null;
                            const selectedMeta = OUTCOME_META[selectedTab.value] || { color: '#078DEE', bg: 'rgba(7,141,238,0.10)' };
                            const unselectedTabs = allTabs.filter(t => t.value !== activeTab);
                            return (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                        <Box sx={{
                                            width: 32, height: 32, borderRadius: '8px',
                                            bgcolor: selectedMeta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0, border: `1.5px solid ${selectedMeta.color}`,
                                            transition: 'all 0.35s ease',
                                        }}>
                                            <Iconify icon={selectedTab.icon.props.icon} width={16} height={16} sx={{ color: selectedMeta.color }} />
                                        </Box>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.012em', lineHeight: 1.3 }}>
                                            {selectedTab.value}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                        {unselectedTabs.map((tab) => {
                                            const m = OUTCOME_META[tab.value] || { color: '#078DEE', bg: 'rgba(7,141,238,0.10)' };
                                            return (
                                                <Tooltip key={tab.value} title={tab.value} placement="bottom" TransitionComponent={Zoom} TransitionProps={{ timeout: 250 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setActiveTab(tab.value)}
                                                        sx={{
                                                            width: 32, height: 32, borderRadius: '8px',
                                                            bgcolor: m.bg, border: '1px solid transparent',
                                                            transition: 'border-color 250ms cubic-bezier(0.32, 0.72, 0, 1), background-color 250ms cubic-bezier(0.32, 0.72, 0, 1), transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                            '&:hover': { borderColor: m.color, bgcolor: `${m.color}26` },
                                                            '&:active': { transform: 'scale(0.92)' },
                                                        }}
                                                    >
                                                        <Iconify icon={tab.icon.props.icon} width={15} height={15} sx={{ color: m.color }} />
                                                    </IconButton>
                                                </Tooltip>
                                            );
                                        })}
                                    </Stack>
                                </>
                            );
                        })()}
                    </Box>
                </Box>
            </Box>
            {activeTab && (
                <Box sx={{ mb: 2, p: 3, bgcolor: '#ffffff', borderRadius: '12px', width: '100%' }}>
                    {getAttendedComponents().find(t => t.value === activeTab)?.component}
                </Box>
            )}
            <Box
                sx={[attendedFooterStyle, getIsVisible(), {
                    pb: 1.5,
                    pt: 1.5,
                    bgcolor: 'rgba(246, 246, 248, 0.82)',
                    backdropFilter: 'saturate(180%) blur(20px)',
                    WebkitBackdropFilter: 'saturate(180%) blur(20px)',
                    borderTop: '0.5px solid rgba(0, 0, 0, 0.10)',
                }]}
            >
                <Container
                    maxWidth={settings.themeStretch ? false : 'xl'}
                    sx={{ mt: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}
                >
                    <Button
                        variant='text'
                        onClick={() => handleClose && handleClose('close')}
                        sx={{
                            color: '#1d1d1f',
                            fontSize: '13px',
                            fontWeight: 500,
                            letterSpacing: '-0.01em',
                            textTransform: 'none',
                            px: 2,
                            py: 0.75,
                            borderRadius: '6px',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                        }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={isLoader}
                        loadingPosition='start'
                        onClick={handleNext}
                        disabled={isDisabledTabForFinishButton(clinCheckData?.status, currentTab, activeTab, isPaymentMandatory, depositPendingStatus, type) || isLoader}
                        sx={{
                            bgcolor: '#00AB55',
                            color: '#ffffff',
                            fontSize: '13px',
                            fontWeight: 600,
                            letterSpacing: '-0.01em',
                            textTransform: 'none',
                            borderRadius: '6px',
                            boxShadow: 'none',
                            px: 2.25,
                            py: 0.75,
                            minWidth: '88px',
                            '&:hover': { bgcolor: '#02934A', boxShadow: 'none' },
                            '&:active': { bgcolor: '#017A3D' },
                            '&.Mui-disabled': { bgcolor: 'rgba(0, 171, 85, 0.35)', color: '#ffffff' },
                        }}
                    >
                        Finish
                    </LoadingButton>
                </Container>
            </Box>
        </Box>
    );
});

AttendedAppointment.propTypes = {
    type: PropTypes.string,
    id: PropTypes.number,
    handleClose: PropTypes.func,
    user: PropTypes.object,
    OpenAssignTask: PropTypes.func,
    editDetailHandler: PropTypes.func,
    handleCloseAndReload: PropTypes.func,
    handleReloadLeads: PropTypes.func,
    isFinishButtonAvailable: PropTypes.bool,
    isOverview: PropTypes.bool,
    handleNoAttended: PropTypes.func,
    /** Switches the host popup's right sidebar tab (e.g. 'email' / 'whatsapp') — used by V2 follow-up. */
    onOpenSidebarTab: PropTypes.func,
};

export default AttendedAppointment;
